import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { SamplingRecordService } from 'src/app/_services/sampling-record.service';
import { BlDetails, TableConfig } from 'src/app/interfaces/bl.interfaces';
import { Sampling, SamplingForm } from 'src/app/interfaces/sampling-record.interfaces';

interface Overlay {
  message:     string;
  icon:        string;
  time?:        number;
  buttons:     boolean;
}

enum Action {
  Detail,
  Create,
  Update,
  None,
}

/**
 * Componente que administra los registros de pre-muestreo de un BL: lista las
 * muestras, permite crearlas, consultarlas, actualizarlas y eliminarlas, y
 * valida que la fecha de muestreo sea posterior a la fecha de atraque.
 */
@Component({
  selector: 'app-pre-sampling-record',
  templateUrl: './pre-sampling-record.component.html',
  styleUrls: ['./pre-sampling-record.component.css']
})
export class PreSamplingRecordComponent implements OnChanges {
  @Input() blDetails!: BlDetails;

  // Overlay and loaders
  overlay: boolean = false;
  overlayForm: boolean = false;
  overlayMessage: boolean = false;
  message: string = '';
  icon: string = '';
  buttons: boolean = false;
  loading: boolean = false;
  action = Action;

  // Table
  table_config: TableConfig = {
    tableHeads: [
      ['Id', 'id'],
      ['No. de oficio', 'trade_number'],
      ['Fecha', 'sampling_date'],
      ['Peso', 'sample_weight'],
      ['Creado por', 'creation_user'],
    ],
    tableHeadsContainer: ['Detalle', 'Editar', 'Eliminar'],
    styles: {
      table_heads: {
        'grid-template-columns': '50px repeat(4, minmax(80px, 1fr)) minmax(200px, auto)',
        'min-width': '570px',
        'margin': '0',
        'padding': '0',
      },
      table_row: {
        'grid-template-columns': '50px repeat(4, minmax(80px, 1fr)) minmax(200px, auto)',
        'min-width': '570px',
      },
      container_icons: {
        'grid-template-columns': 'repeat(3, 1fr)',
      }
    },
    arrayFields: [
      'id',
      'trade_number',
      'sampling_date',
      'sample_weight',
      'creation_user',
    ],
    iconos: [["/detalle-export-entry", "id", 'bi bi-eye'],["/actualizar-export-entry", "id", 'bi bi-pencil-square'], ["/eliminar-entry-export", "id", 'bi bi-trash3']],
    mensaje: "Sin muestras asignadas.",
    filter: false,
    totals: {
      show: false,
      content: ['', '', 'Totales:', 'total', '', '',],
      total: 0,
    }
  };
  loaderTable: boolean = false;
  samplingRecords: Sampling[] = [];

  // Sampling Form
  samplingForm: FormGroup;
  currentSamplingId: number = 0;
  canSave: boolean = false;
  isGetSamplingRecordsCalled: boolean = false;

  constructor(
    private fb: FormBuilder,
    private samplingRecordSvc: SamplingRecordService
  ) {
    this.samplingForm = this.fb.group({
      trade_number: [, [Validators.required]],
      sampling_date: [, [Validators.required]],
      sample_weight: [, [Validators.required, Validators.pattern(/^(?!0\d)\d{0,11}(?:\.\d{1,3})?$/)]],
    });
  }

  /**
   * Carga los registros de muestreo cuando el BL recibido tiene un número de operación válido.
   * @param changes - Cambios detectados en las propiedades de entrada del componente.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['blDetails'].isFirstChange() && changes['blDetails'].currentValue.operation_number && !this.isGetSamplingRecordsCalled) {
      this.getSamplingRecords();
    }
  }

  /**
   * Valida el formulario y crea o actualiza el registro de muestreo según corresponda.
   */
  saveSampling() {
    if (this.samplingForm.invalid) {
      this.samplingForm.markAllAsTouched();
      return;
    }
    const sampling: SamplingForm = {
      operation_id: this.blDetails.operation_id,
      trade_number: String(this.samplingForm.value.trade_number),
      sampling_date: String(this.samplingForm.value.sampling_date),
      sample_weight: Number(this.samplingForm.value.sample_weight)
    };
    if (!this.currentSamplingId)
      this.createSamplingRecord(sampling);
    else
      this.updateSamplingRecord(sampling);
  }

  /**
   * Muestra la confirmación de eliminación y guarda el identificador de la muestra a eliminar.
   * @param id - Identificador de la muestra a eliminar.
   */
  deleteConfirm(id: number) {
    this.handleOverlay({
      message: '¿Estás seguro de eliminar esta muestra?',
      icon: '',
      buttons: true,
      time: 0
    });
    this.currentSamplingId = id;
  }

  /**
   * Crea un nuevo registro de muestreo y refresca la lista al completarse.
   * @param sampling - Datos del registro de muestreo a crear.
   */
  createSamplingRecord(sampling: SamplingForm) {
    this.loaderTable = true;
    this.samplingRecordSvc.createSamplingRecord(sampling)
    .subscribe({
      next: (response) => {
        this.loaderTable = false;
        let message = 'Muestra guardada correctamente';
        let icon = 'bi-check2';
        if (typeof response === 'object' && response.error) {
          message = 'Error al guardar la muestra, favor de intentarlo más tarde';
          icon = 'bi-exclamation-circle-fill';
        }
        this.handleOverlay({
          message: message,
          icon: icon,
          buttons: false,
          time: 2000
        });
        this.getSamplingRecords();
      }, error: (error) => {
        this.loaderTable = false;
        this.handleOverlay({
          message: 'Error en el servidor al guardar la muestra, favor de intentarlo más tarde',
          icon: 'bi-exclamation-circle-fill',
          buttons: false,
          time: 3000
        });
      }
    });
    this.toggleOverlayForm(this.action.None);
  }

  /**
   * Actualiza un registro de muestreo existente y refresca la lista al completarse.
   * @param sampling - Datos del registro de muestreo a actualizar.
   */
  updateSamplingRecord(sampling: SamplingForm) {
    sampling.id = this.currentSamplingId;
    this.loaderTable = true;
    this.samplingRecordSvc.updateSamplingRecord(sampling)
    .subscribe({
      next: (response) => {
        this.loaderTable = false;
        let message = 'Muestra actualizada correctamente';
        let icon = 'bi-check2';
        if (typeof response === 'object' && response.error) {
          message = 'Error al actualizar la muestra, favor de intentarlo más tarde';
          icon = 'bi-exclamation-circle-fill';
        }
        this.handleOverlay({
          message: message,
          icon: icon,
          buttons: false,
          time: 2000
        });
        this.getSamplingRecords();
      }, error: (error) => {
        this.loaderTable = false;
        this.handleOverlay({
          message: 'Error en el servidor al actualizar la muestra, favor de intentarlo más tarde',
          icon: 'bi-exclamation-circle-fill',
          buttons: false,
          time: 3000
        });
      }
    });
    this.toggleOverlayForm(this.action.None);
  }

  /**
   * Elimina el registro de muestreo seleccionado, salvo que la operación se cancele.
   * @param cancel - Cadena no vacía cuando el usuario cancela la eliminación.
   */
  deleteSampling(cancel: string) {
    this.overlayMessage = false;
    if (cancel)
      return;
    this.loaderTable = true;
    this.samplingRecordSvc.deleteSamplingRecord(this.currentSamplingId)
    .subscribe({
      next: (response) => {
        this.loaderTable = false;
        let message = 'Muestra eliminada correctamente';
        let icon = 'bi-check2';
        if (!Array.isArray(response)) {
          message = 'Error al eliminar la muestra, favor de intentarlo más tarde';
          icon = 'bi-exclamation-circle-fill';
        }
        this.handleOverlay({
          message: message,
          icon: icon,
          buttons: false,
          time: 2000
        });
        this.getSamplingRecords();
      }, error: (error) => {
        this.loaderTable = false;
        this.handleOverlay({
          message: 'Error en el servidor al eliminar la muestra, favor de intentarlo más tarde',
          icon: 'bi-exclamation-circle-fill',
          buttons: false,
          time: 3000
        });
      }
    });
  }

  /**
   * Consulta los registros de muestreo del BL actual y los formatea para la tabla.
   */
  getSamplingRecords() {
    this.loaderTable = true;
    this.samplingRecordSvc.getSamplingRecords(this.blDetails.operation_id).subscribe({
      next: (samplingRecords) => {
        this.loaderTable = false;
        if (Array.isArray(samplingRecords)) {
          this.samplingRecords = [...samplingRecords.map(sampling => ({
            ...sampling,
            creation_user: sampling.creation_user.split('@')[0]
          }))];
          this.isGetSamplingRecordsCalled = true;
        } else {
          this.samplingRecords = [];
          this.handleOverlay({
            message: 'Error al obtener las muestras, favor de intentarlo más tarde',
            icon: 'bi-exclamation-circle-fill',
            buttons: false,
            time: 3000
          });
        }
      }, error: (error) => {
        this.loaderTable = false;
        this.handleOverlay({
          message: 'Error con el servidor, favor de intentarlo más tarde',
          icon: 'bi-exclamation-circle-fill',
          buttons: false,
          time: 3000
        });
      }
    });
  }

  /**
   * Alterna la visibilidad del overlay principal.
   */
  toggleOverlay() {
    this.overlay = !this.overlay;
  }

  /**
   * Alterna y prepara el formulario de muestreo según la acción solicitada (crear, actualizar o detalle).
   * @param action - Acción a ejecutar sobre el formulario.
   * @param id - Identificador de la muestra, requerido para actualizar o ver detalle.
   */
  toggleOverlayForm(action: Action, id?: number) {
    this.samplingForm.enable();
    this.canSave = action === Action.Detail ? false : true;
    switch (action) {
      case Action.Create:
        this.samplingForm.reset();
        this.currentSamplingId = 0;
        break;
      case Action.Update:
        this.formWithCurrentSampling(id || 0);
        break;
      case Action.Detail:
        this.formWithCurrentSampling(id || 0);
        this.samplingForm.disable();
        break;
      case Action.None:
        break;
      default:
        break;
    }
    this.overlayForm = !this.overlayForm;
  }

  /**
   * Indica si un campo del formulario es inválido y ha sido tocado.
   * @param campo - Nombre del campo a evaluar.
   * @returns `true` si el campo tiene errores y fue tocado; en caso contrario `null` o `false`.
   */
  isValidField( campo: string ): boolean | null {
    return this.samplingForm.get(campo)!.errors &&
    this.samplingForm.get(campo)!.touched;
  }

  /**
   * Configura y muestra el overlay de mensaje con los datos indicados.
   * @param param - Objeto con el mensaje, ícono, indicador de botones y tiempo opcional de cierre.
   */
  handleOverlay({ message, icon, buttons, time }: Overlay): void {
    this.message = message;
    this.icon = icon;
    this.buttons = buttons;
    this.overlayMessage = true;
    if (time && time > 0) {
      setTimeout(() => {
        this.overlayMessage = false;
      }, time);
    }
  }

  /**
   * Rellena el formulario con los datos del registro de muestreo indicado.
   * @param id - Identificador de la muestra cuyos datos se cargan.
   */
  formWithCurrentSampling(id: number) {
    const currentSampling = this.samplingRecords.find(sampling => sampling.id === id);
    this.currentSamplingId = id || 0;
    if (currentSampling) {
      this.samplingForm.reset({
        trade_number: currentSampling.trade_number,
        sampling_date: currentSampling.sampling_date,
        sample_weight: currentSampling.sample_weight,
      });
    }
  }

  /**
   * Crea un validador que exige que la fecha de muestreo sea posterior a la fecha de atraque del BL.
   * @returns Una función validadora que retorna `{ errorDate: true }` si la fecha no es válida, o `null` en caso contrario.
   */
  dateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.blDetails) {
        return null
      }
      const limitDate = new Date(this.blDetails.docking_date);
      const date = new Date(control.value);
      if (isNaN(date.getTime())) {
        // Si no es una fecha válida
        return null;
      }
      return date > limitDate ? null : { errorDate: true };
    };
  }

}
